import React, { useEffect, useState, useRef } from 'react';
import { MasterTransaction, ShortfallAllocation } from '../types';
import { Staff } from '../services/staffService';
import { useFirebase } from '../contexts/FirebaseContext';
import { approveMasterTransactionByEmployee, getMasterTransactionsByStaffId, getMasterTransactionsByEmail } from '../services/allocationService';
import { approveShortfallAllocationByEmployee, getShortfallAllocationsByStaffId } from '../services/grossReceiptsService';
import { getPayrollPeriods } from '../services/payrollPeriodService';
import { getIRSCashTipDeadline, formatLongDate } from '../lib/complianceUtils';
import { isApprovedOrAllocatedForStaff, isDashboardSubmitted, isAllocated, isSubmitted, isRelevantForApproval } from '../lib/transactionUtils';
import { formatCurrency } from '../lib/formatters';
import { Check, PenTool, Eraser, AlertCircle, FileCheck, ShieldCheck, Fingerprint, Calendar, Info, Clock, ChevronDown, ChevronUp, Filter, RotateCcw, DollarSign, CalendarRange, Receipt } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface GroupedPeriod {
  id: string; // e.g. "2026-06-10_2026-06-24"
  startDate: string;
  endDate: string;
  label: string;
  transactions: MasterTransaction[];
  shortfalls: ShortfallAllocation[];
  totalSubmittedTips: number;
  posSubmitted: number;
  cashSubmitted: number;
  otherSubmitted: number;
  shiftCount: number;
  monthTotal: number;
  ytdTotal: number;
  totalAllocatedTips: number;
  totalShortfall: number;
  grandTotal: number;
  isApproved: boolean;
}

export const EmployeeApprovals: React.FC<{ staff: Staff | null }> = ({ staff }) => {
  const { user, tenantId } = useFirebase();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<MasterTransaction[]>([]);
  const [shortfalls, setShortfalls] = useState<ShortfallAllocation[]>([]);
  const [groupedPeriods, setGroupedPeriods] = useState<GroupedPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  
  // Date Query Filter states
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [activePreset, setActivePreset] = useState<'all' | 'this_month' | 'last_month' | 'ytd' | 'custom'>('all');
  
  // Sign panel states
  const [activeSignType, setActiveSignType] = useState<'type' | 'draw'>('type');
  const [typedName, setTypedName] = useState('');
  const [drawnData, setDrawnData] = useState('');
  const [perjuryCertified, setPerjuryCertified] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isNotesExpanded, setIsNotesExpanded] = useState(true);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const getTxAmount = (t: MasterTransaction) => {
    return (t.submittedAmount !== undefined ? t.submittedAmount : (t.amount || 0));
  };

  const isTxPOS = (t: MasterTransaction) => {
    return t.source === 'POS' || t.source === 'POS Override' || t.reportingStatus === 'pos_submitted';
  };

  const isTxCash = (t: MasterTransaction) => {
    return t.source === 'Cash' || t.source === 'Jar' || t.reportingStatus === 'cash_submitted' || t.reportingStatus === 'jar_submitted';
  };

  const handlePresetChange = (preset: 'all' | 'this_month' | 'last_month' | 'ytd') => {
    setActivePreset(preset);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-indexed

    if (preset === 'all') {
      setFromDate('');
      setToDate('');
    } else if (preset === 'this_month') {
      const monthStr = (month + 1).toString().padStart(2, '0');
      const lastDay = new Date(year, month + 1, 0).getDate();
      setFromDate(`${year}-${monthStr}-01`);
      setToDate(`${year}-${monthStr}-${lastDay.toString().padStart(2, '0')}`);
    } else if (preset === 'last_month') {
      const lastMonthDate = new Date(year, month - 1, 1);
      const lmYear = lastMonthDate.getFullYear();
      const lmMonth = lastMonthDate.getMonth();
      const lmMonthStr = (lmMonth + 1).toString().padStart(2, '0');
      const lastDay = new Date(lmYear, lmMonth + 1, 0).getDate();
      setFromDate(`${lmYear}-${lmMonthStr}-01`);
      setToDate(`${lmYear}-${lmMonthStr}-${lastDay.toString().padStart(2, '0')}`);
    } else if (preset === 'ytd') {
      setFromDate(`${year}-01-01`);
      const monthStr = (month + 1).toString().padStart(2, '0');
      const dayStr = now.getDate().toString().padStart(2, '0');
      setToDate(`${year}-${monthStr}-${dayStr}`);
    }
  };

  const clearFilters = () => {
    setActivePreset('all');
    setFromDate('');
    setToDate('');
  };

  const loadData = async () => {
    console.log('DEBUG [EmployeeApprovals] loadData called, tenantId:', tenantId, 'staff:', staff);
    if (!tenantId || !staff?.id) {
      console.log('DEBUG [EmployeeApprovals] Missing tenantId or staff.id', { tenantId, staffId: staff?.id });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      console.log('DEBUG [EmployeeApprovals] Fetching data for staff:', staff.id, staff.email);
      const [txDataById, txDataByEmail, sfData, periods] = await Promise.all([
        getMasterTransactionsByStaffId(tenantId, staff.id),
        getMasterTransactionsByEmail(tenantId, staff.email),
        getShortfallAllocationsByStaffId(tenantId, staff.id),
        getPayrollPeriods(tenantId)
      ]);

      console.log('DEBUG [EmployeeApprovals] Data fetched:', { 
        txDataByIdCount: txDataById.length, 
        txDataByEmailCount: txDataByEmail.length,
        shortfallCount: sfData.length 
      });

      const txData = [...txDataById, ...txDataByEmail.filter(t => !txDataById.find(existing => existing.id === t.id))];

      // Set all transactions for the staff
      setTransactions(txData);
      setShortfalls(sfData);
      
      // All relevant transactions for the employee across all periods
      const allRelevantTxs = txData.filter(tx => isRelevantForApproval(tx, staff.id, staff.email, txData));

      // Group them into periods by matching against configured payroll periods,
      // falling back to semi-monthly cycles if no matching period is defined.
      const groups: Record<string, { txs: MasterTransaction[]; sfs: ShortfallAllocation[] }> = {};

      const getPeriodKey = (dateStr: string) => {
        if (!dateStr || dateStr.length < 10) return { key: 'Other', start: '', end: '', label: 'Other Allocations' };

        // 1. Try matching with configured payroll periods first
        const matched = (periods || []).find(p => dateStr >= p.startDate && dateStr <= p.endDate);
        if (matched) {
          const [sYear, sMonth, sDay] = matched.startDate.split('-').map(Number);
          const [eYear, eMonth, eDay] = matched.endDate.split('-').map(Number);
          const startD = new Date(sYear, sMonth - 1, sDay);
          const endD = new Date(eYear, eMonth - 1, eDay);
          const label = `${startD.toLocaleString('en-US', { month: 'long', day: 'numeric' })}–${endD.toLocaleString('en-US', { month: 'long', day: 'numeric' })}, ${startD.getFullYear()}`;
          return {
            key: `${matched.startDate}_${matched.endDate}`,
            start: matched.startDate,
            end: matched.endDate,
            label: label
          };
        }

        // 2. Fallback to standard semi-monthly periods
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const monthNum = month;
        const monthStr = monthNum.toString().padStart(2, '0');
        
        if (day <= 15) {
          const sDate = new Date(year, month - 1, 1);
          const eDate = new Date(year, month - 1, 15);
          return {
            key: `${year}-${monthStr}-01_${year}-${monthStr}-15`,
            start: `${year}-${monthStr}-01`,
            end: `${year}-${monthStr}-15`,
            label: `${sDate.toLocaleString('en-US', { month: 'long' })} 01–${eDate.toLocaleString('en-US', { month: 'long' })} 15, ${year}`
          };
        } else {
          const lastDay = new Date(year, month, 0).getDate();
          const sDate = new Date(year, month - 1, 16);
          const eDate = new Date(year, month - 1, lastDay);
          return {
            key: `${year}-${monthStr}-16_${year}-${monthStr}-${lastDay}`,
            start: `${year}-${monthStr}-16`,
            end: `${year}-${monthStr}-${lastDay}`,
            label: `${sDate.toLocaleString('en-US', { month: 'long' })} 16–${eDate.toLocaleString('en-US', { month: 'long' })} ${lastDay}, ${year}`
          };
        }
      };

      // Group standard transactions
      txData.forEach(tx => {
        const { key, start, end, label } = getPeriodKey(tx.tipDate);
        if (!groups[key]) groups[key] = { txs: [], sfs: [] };
        groups[key].txs.push(tx);
      });

      // Group shortfall allocations
      sfData.forEach(sf => {
        const start = sf.startDate;
        const end = sf.endDate;
        const startD = new Date(start + 'T00:00:00');
        const endD = new Date(end + 'T00:00:00');
        const key = `${start}_${end}`;
        const label = `${startD.toLocaleString('en-US', { month: 'long', day: '2-digit' })}–${endD.toLocaleString('en-US', { month: 'long', day: '2-digit' })}, ${startD.getFullYear()}`;
        if (!groups[key]) groups[key] = { txs: [], sfs: [] };
        groups[key].sfs.push(sf);
      });

      // Compile GroupedPeriods
      const compiled: GroupedPeriod[] = Object.entries(groups).map(([key, item]) => {
        const parts = key.split('_');
        const startDate = parts[0];
        const endDate = parts[1] || parts[0];
        
        // Find label
        let label = '';
        if (item.txs.length > 0) {
          label = getPeriodKey(item.txs[0].tipDate).label;
        } else if (item.sfs.length > 0) {
          const startD = new Date(item.sfs[0].startDate + 'T00:00:00');
          const endD = new Date(item.sfs[0].endDate + 'T00:00:00');
          label = `${startD.toLocaleString('en-US', { month: 'long', day: '2-digit' })}–${endD.toLocaleString('en-US', { month: 'long', day: '2-digit' })}, ${startD.getFullYear()}`;
        } else {
          label = 'Other Period';
        }

        const relevantTxs = item.txs.filter(tx => isRelevantForApproval(tx, staff.id, staff.email, txData));
        
        // Breakdowns of submitted tips
        const posSubmitted = relevantTxs
          .filter(t => isTxPOS(t))
          .reduce((sum, t) => sum + getTxAmount(t), 0);

        const cashSubmitted = relevantTxs
          .filter(t => isTxCash(t))
          .reduce((sum, t) => sum + getTxAmount(t), 0);

        const otherSubmitted = relevantTxs
          .filter(t => !isTxPOS(t) && !isTxCash(t))
          .reduce((sum, t) => sum + getTxAmount(t), 0);

        const totalSubmittedTips = posSubmitted + cashSubmitted + otherSubmitted;
        const totalAllocatedTips = relevantTxs.filter(tx => tx.type === 'Allocated').reduce((sum, tx) => sum + (tx.amount || 0), 0);
        const totalShortfall = item.sfs.reduce((sum, sf) => sum + (sf.allocatedShortfall || 0), 0);
        
        // Shift count
        const shiftCount = new Set(relevantTxs.map(t => `${t.tipDate}_${t.shiftName || t.shift || t.id}`)).size;

        // Month and YTD totals for the employee relative to this period
        const periodYear = startDate ? startDate.substring(0, 4) : new Date().getFullYear().toString();
        const periodMonth = startDate ? startDate.substring(0, 7) : `${periodYear}-01`;

        const monthTotal = allRelevantTxs
          .filter(t => t.tipDate?.startsWith(periodMonth))
          .reduce((sum, t) => sum + getTxAmount(t), 0);

        const ytdTotal = allRelevantTxs
          .filter(t => t.tipDate?.startsWith(periodYear) && (endDate ? t.tipDate <= endDate : true))
          .reduce((sum, t) => sum + getTxAmount(t), 0);

        // A period is marked as approved if ALL allocated/submitted transactions and shortfall allocations in it have been approved
        const txsApproved = relevantTxs.length > 0 ? relevantTxs.every(tx => tx.employeeApproved === true) : true;
        const sfsApproved = item.sfs.length > 0 ? item.sfs.every(sf => sf.employeeApproved === true) : true;
        const isApproved = txsApproved && sfsApproved;

        return {
          id: key,
          startDate,
          endDate,
          label,
          transactions: item.txs,
          shortfalls: item.sfs,
          totalSubmittedTips,
          posSubmitted,
          cashSubmitted,
          otherSubmitted,
          shiftCount,
          monthTotal,
          ytdTotal,
          totalAllocatedTips,
          totalShortfall,
          grandTotal: totalAllocatedTips + totalShortfall,
          isApproved
        };
      }).sort((a, b) => b.id.localeCompare(a.id));

      setGroupedPeriods(compiled);
      
      // Select first unapproved period by default, or the first one if all are approved
      if (compiled.length > 0) {
        const firstUnapproved = compiled.find(p => !p.isApproved);
        setSelectedPeriodId(firstUnapproved ? firstUnapproved.id : compiled[0].id);
      }
    } catch (err) {
      console.error('Error compiling approvals data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [tenantId, staff?.id]);

  const activePeriod = groupedPeriods.find(p => p.id === selectedPeriodId);

  // Filter grouped periods based on activeTab and date range query
  const filteredPeriods = groupedPeriods.filter((period) => {
    // 1. Tab match
    const matchesTab = activeTab === 'pending' ? !period.isApproved : period.isApproved;
    if (!matchesTab) return false;

    // 2. From date query
    if (fromDate && period.endDate && period.endDate < fromDate) {
      return false;
    }

    // 3. To date query
    if (toDate && period.startDate && period.startDate > toDate) {
      return false;
    }

    return true;
  });

  // Calculate cumulative query totals across filtered periods
  const queryTotals = filteredPeriods.reduce(
    (acc, p) => {
      acc.submitted += p.totalSubmittedTips;
      acc.pos += p.posSubmitted;
      acc.cash += p.cashSubmitted;
      acc.shifts += p.shiftCount;
      return acc;
    },
    { submitted: 0, pos: 0, cash: 0, shifts: 0 }
  );

  // Drawing Canvas Methods
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    const rect = canvas.getBoundingClientRect();
    let clientX = 0, clientY = 0;
    if ('touches' in e) {
      if (e.touches.length === 0) return;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let clientX = 0, clientY = 0;
    if ('touches' in e) {
      if (e.touches.length === 0) return;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setDrawnData(canvas.toDataURL());
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawnData('');
  };

  const handleSignPeriod = async () => {
    console.log('DEBUG: handleSignPeriod called');
    if (!activePeriod || !perjuryCertified || !tenantId) {
      console.log('DEBUG: Signoff blocked by conditions', { activePeriod: !!activePeriod, perjuryCertified, tenantId: !!tenantId });
      return;
    }
    if (activeSignType === 'type' && !typedName.trim()) {
      console.log('DEBUG: Signoff blocked: type signature empty');
      return;
    }
    if (activeSignType === 'draw' && !drawnData) {
      console.log('DEBUG: Signoff blocked: drawn signature empty');
      return;
    }

    setSubmitting(true);
    try {
      console.log('DEBUG: Starting signoff for period', activePeriod.id);
      
      // 1. Approve all Allocated Master Transactions in the period
      console.log('DEBUG: All transactions in period:', activePeriod.transactions);
      const txsToApprove = activePeriod.transactions
        .filter(t => {
          const relevant = isRelevantForApproval(t, staff!.id, staff!.email, transactions);
          const approved = t.employeeApproved;
          console.log(`DEBUG: TX ${t.id}, relevant: ${relevant}, approved: ${approved}`);
          return relevant && !approved;
        });
      console.log('DEBUG: Transactions to approve:', txsToApprove.length);
      
      const txPromises = txsToApprove
        .map(t => approveMasterTransactionByEmployee(
          tenantId,
          t.id,
          activeSignType,
          activeSignType === 'type' ? typedName.trim() : undefined,
          activeSignType === 'draw' ? drawnData : undefined
        ));

      // 2. Approve all Shortfall Allocations in the period
      const sfsToApprove = activePeriod.shortfalls
        .filter(sf => !sf.employeeApproved);
      console.log('DEBUG: Shortfalls to approve:', sfsToApprove.length);

      const sfPromises = sfsToApprove
        .map(sf => approveShortfallAllocationByEmployee(
          tenantId,
          sf.id!,
          activeSignType,
          activeSignType === 'type' ? typedName.trim() : undefined,
          activeSignType === 'draw' ? drawnData : undefined
        ));

      if (txsToApprove.length === 0 && sfsToApprove.length === 0) {
        console.warn('DEBUG: No transactions or shortfalls found to approve in this period!');
        setSubmitting(false);
        alert('All transactions in this period are already signed/approved.');
        return;
      }

      await Promise.all([...txPromises, ...sfPromises]);

      // Reset states
      setTypedName('');
      setDrawnData('');
      setPerjuryCertified(false);
      
      // Reload everything
      await loadData();
    } catch (error) {
      console.error('Error signing off allocations:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!staff) {
    console.log('DEBUG [EmployeeApprovals] staff is null');
    return (
      <div className="flex items-center justify-center p-20 text-gray-500 font-medium">
        Unable to load approval data: No staff profile found for this user.
      </div>
    );
  }

  if (!tenantId) {
    console.log('DEBUG [EmployeeApprovals] tenantId is null');
    return (
      <div className="flex items-center justify-center p-20 text-gray-500 font-medium">
        Unable to load approval data: No tenant found.
      </div>
    );
  }

  // Calculate 10th-day compliance warning for the period
  const getComplianceStatus = (period: GroupedPeriod) => {
    if (!period.endDate) return { isLate: false, deadlineStr: '' };
    const end = new Date(period.endDate);
    const deadline = getIRSCashTipDeadline(end.getFullYear(), end.getMonth());
    const isLate = new Date() > deadline;
    return {
      isLate,
      deadlineStr: formatLongDate(deadline)
    };
  };

  const compliance = activePeriod ? getComplianceStatus(activePeriod) : { isLate: false, deadlineStr: '' };

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic">Approval & Sign-off</h1>
          <p className="text-gray-500 mt-1 font-medium">Review and certify your submitted cash and POS tips for each payroll period.</p>
        </div>
        {staff && (
          <div className="bg-white border border-gray-100 px-4 py-2.5 rounded-2xl shadow-2xs text-left sm:text-right">
            <p className="text-sm font-bold text-gray-900">{staff.name}</p>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">POS ID: {staff.posId || 'N/A'}</p>
          </div>
        )}
      </header>

      {/* Query Filter & Search Controls */}
      <div className="bg-white border border-gray-100 rounded-3xl p-5 shadow-2xs space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Tab Selector */}
          <div className="flex gap-1.5 bg-gray-100/80 p-1.5 rounded-2xl w-fit">
            <button
              onClick={() => {
                setActiveTab('pending');
                setSelectedPeriodId(null);
              }}
              className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                activeTab === 'pending' 
                  ? 'bg-white text-gray-900 shadow-xs' 
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Clock size={13} className={activeTab === 'pending' ? 'text-amber-600' : ''} />
              <span>Pending Sign-off</span>
            </button>
            <button
              onClick={() => {
                setActiveTab('history');
                setSelectedPeriodId(null);
              }}
              className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                activeTab === 'history' 
                  ? 'bg-white text-gray-900 shadow-xs' 
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <ShieldCheck size={13} className={activeTab === 'history' ? 'text-emerald-600' : ''} />
              <span>Approval History</span>
            </button>
          </div>

          {/* Quick Presets */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mr-1 flex items-center gap-1">
              <CalendarRange size={12} /> Range:
            </span>
            {[
              { id: 'all', label: 'All Periods' },
              { id: 'this_month', label: 'This Month' },
              { id: 'last_month', label: 'Last Month' },
              { id: 'ytd', label: 'YTD' }
            ].map(preset => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handlePresetChange(preset.id as any)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  activePreset === preset.id
                    ? 'bg-blue-600 text-white shadow-2xs'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Selection Inputs */}
        <div className="pt-3 border-t border-gray-50 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-gray-600">From:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setActivePreset('custom');
              }}
              className="px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-gray-600">To:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setActivePreset('custom');
              }}
              className="px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
            />
          </div>

          {(fromDate || toDate || activePreset !== 'all') && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
            >
              <RotateCcw size={12} /> Clear Filter
            </button>
          )}

          {/* Quick Query Metrics Tag */}
          <div className="ml-auto text-[11px] font-semibold text-gray-500 flex items-center gap-2">
            <span>Query Results: <strong className="text-gray-900">{filteredPeriods.length}</strong> {filteredPeriods.length === 1 ? 'Period' : 'Periods'}</span>
            <span>•</span>
            <span>Total: <strong className="text-emerald-600">{formatCurrency(queryTotals.submitted)}</strong></span>
          </div>
        </div>
      </div>

      {/* Period Cards (Submitted Tips, Month, YTD, Shift count) */}
      <div className="space-y-4">
        <div className="flex justify-between items-center px-1">
          <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
            <Receipt size={14} className="text-gray-400" />
            {activeTab === 'pending' ? 'Pending Submitted Tip Periods' : 'Signed Pay Period Records'}
          </h3>
          <span className="text-xs text-gray-400 font-semibold">
            {filteredPeriods.length} {filteredPeriods.length === 1 ? 'period' : 'periods'} found
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPeriods.map((period) => {
            const isSelected = period.id === selectedPeriodId;
            return (
              <button
                key={period.id}
                type="button"
                onClick={() => {
                  setSelectedPeriodId(period.id);
                  setTypedName('');
                  setDrawnData('');
                  setPerjuryCertified(false);
                }}
                className={`text-left p-5 rounded-[1.5rem] border transition-all flex flex-col justify-between gap-4 cursor-pointer ${
                  isSelected 
                    ? 'bg-slate-900 border-slate-900 text-white shadow-xl ring-2 ring-blue-500/20' 
                    : 'bg-white border-gray-100 hover:border-gray-300 text-gray-900 hover:bg-gray-50/80 shadow-2xs'
                }`}
              >
                {/* Header Tag & Status Badge */}
                <div className="space-y-1.5 w-full">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${
                      isSelected ? 'bg-slate-800 text-blue-400 border border-slate-700' : 'bg-blue-50 text-blue-700 border border-blue-100'
                    }`}>
                      Pay Period
                    </span>
                    {period.isApproved ? (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isSelected ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
                      }`}>
                        <ShieldCheck size={12} /> Signed & Approved
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isSelected ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700 border border-amber-200/60'
                      }`}>
                        <Clock size={12} /> Pending Sign-off
                      </span>
                    )}
                  </div>
                  <h4 className="font-bold text-sm tracking-tight">{period.label}</h4>
                </div>

                {/* Primary Submitted Total & Source Breakdown */}
                <div className={`p-3 rounded-xl w-full ${isSelected ? 'bg-slate-800/80 border border-slate-700' : 'bg-gray-50 border border-gray-100'}`}>
                  <div className="flex items-baseline justify-between">
                    <span className={`text-[10px] font-black uppercase tracking-wider ${isSelected ? 'text-slate-400' : 'text-gray-400'}`}>
                      Total Submitted
                    </span>
                    <span className={`text-base font-black ${isSelected ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {formatCurrency(period.totalSubmittedTips)}
                    </span>
                  </div>
                  
                  <div className={`flex items-center gap-2 mt-2 pt-2 border-t border-dashed ${isSelected ? 'border-slate-700' : 'border-gray-200'} text-[10px]`}>
                    <span className={`font-semibold ${isSelected ? 'text-slate-300' : 'text-gray-600'}`}>
                      POS: <strong className={isSelected ? 'text-white' : 'text-gray-900'}>{formatCurrency(period.posSubmitted)}</strong>
                    </span>
                    <span className={isSelected ? 'text-slate-600' : 'text-gray-300'}>•</span>
                    <span className={`font-semibold ${isSelected ? 'text-slate-300' : 'text-gray-600'}`}>
                      Cash: <strong className={isSelected ? 'text-white' : 'text-gray-900'}>{formatCurrency(period.cashSubmitted)}</strong>
                    </span>
                  </div>
                </div>

                {/* Month Total, YTD & Shift Count */}
                <div className="flex items-center justify-between text-[10px] pt-1 w-full">
                  <div className={`flex items-center gap-2 font-medium ${isSelected ? 'text-slate-400' : 'text-gray-500'}`}>
                    <span>Month: <strong className={isSelected ? 'text-slate-200' : 'text-gray-800'}>{formatCurrency(period.monthTotal)}</strong></span>
                    <span>|</span>
                    <span>YTD: <strong className={isSelected ? 'text-slate-200' : 'text-gray-800'}>{formatCurrency(period.ytdTotal)}</strong></span>
                  </div>
                  <span className={`font-bold uppercase tracking-wider text-[9px] px-2 py-0.5 rounded-md ${
                    isSelected ? 'bg-slate-800 text-slate-300' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {period.shiftCount} {period.shiftCount === 1 ? 'Shift' : 'Shifts'}
                  </span>
                </div>
              </button>
            );
          })}

          {filteredPeriods.length === 0 && (
            <div className="card p-10 text-center text-gray-400 italic font-medium col-span-full bg-white rounded-3xl border border-gray-100">
              No {activeTab === 'pending' ? 'pending' : 'signed'} periods found for the selected query dates.
            </div>
          )}
        </div>
      </div>

      {/* Ledger Breakdown & Digital Sign-off */}
      <div>
        <AnimatePresence mode="wait">
          {activePeriod ? (
            <motion.div
              key={activePeriod.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Ledger Summary Box */}
              <div className="bg-white border border-gray-100 rounded-[2rem] p-8 shadow-sm space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-50 pb-6">
                  <div>
                    <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase italic">Pay Period Summary & Submitted Tips</h3>
                    <p className="text-xs text-gray-400 font-semibold mt-0.5">Shift earnings and recorded tip submissions for {activePeriod.label}</p>
                  </div>
                  <div>
                    {activePeriod.isApproved ? (
                      <span className="flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-full text-xs font-black uppercase tracking-wider shadow-2xs">
                        <ShieldCheck size={14} /> Signed & Approved
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 px-3.5 py-1.5 bg-amber-50 text-amber-700 border border-amber-200/80 rounded-full text-xs font-black uppercase tracking-wider animate-pulse shadow-2xs">
                        <AlertCircle size={14} /> Action Required
                      </span>
                    )}
                  </div>
                </div>

                {/* Summary Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-2xl">
                    <span className="text-[9px] font-black text-emerald-800 uppercase tracking-widest block">Total Submitted Tips</span>
                    <span className="text-2xl font-black text-emerald-700 mt-1 block">{formatCurrency(activePeriod.totalSubmittedTips)}</span>
                  </div>

                  <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-2xl">
                    <span className="text-[9px] font-black text-blue-800 uppercase tracking-widest block">POS Tips</span>
                    <span className="text-xl font-black text-blue-700 mt-1 block">{formatCurrency(activePeriod.posSubmitted)}</span>
                  </div>

                  <div className="p-4 bg-amber-50/50 border border-amber-100 rounded-2xl">
                    <span className="text-[9px] font-black text-amber-800 uppercase tracking-widest block">Cash & Jar Tips</span>
                    <span className="text-xl font-black text-amber-700 mt-1 block">{formatCurrency(activePeriod.cashSubmitted)}</span>
                  </div>

                  <div className="p-4 bg-purple-50/50 border border-purple-100 rounded-2xl">
                    <span className="text-[9px] font-black text-purple-800 uppercase tracking-widest block">YTD Submitted</span>
                    <span className="text-xl font-black text-purple-700 mt-1 block">{formatCurrency(activePeriod.ytdTotal)}</span>
                  </div>
                </div>

                {/* Shortfall Alert if any */}
                {activePeriod.totalShortfall > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-xs text-red-800 font-medium leading-relaxed">
                    You have <strong className="font-bold text-red-700">{formatCurrency(activePeriod.totalShortfall)}</strong> in safe-harbor shortfall allocations for the period <strong className="text-gray-900">{activePeriod.startDate} to {activePeriod.endDate}</strong>.
                  </div>
                )}

                {/* Individual Transactions Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Individual Shift & Tip Records</h4>
                    <span className="text-[10px] text-gray-400 font-semibold">{activePeriod.transactions.length} Records</span>
                  </div>
                  
                  <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto pr-1 border border-gray-100 rounded-2xl min-w-0">
                    <div className="grid grid-cols-[100px_1fr_100px_1fr_120px] gap-x-4 items-center px-4 py-2 bg-gray-50 border-b border-gray-100 text-[9px] font-black text-gray-400 uppercase tracking-widest sticky top-0 bg-gray-50 z-10 min-w-0">
                      <span className="text-left">Date</span>
                      <span className="text-right">Your Submitted Tips</span>
                      <span className="text-right">TX ID</span>
                      <span className="text-left">Shift</span>
                      <span className="text-left">Source</span>
                    </div>
                    {(() => {
                      const filteredTxs = [...activePeriod.transactions].filter(tx => {
                        const isRelevant = isRelevantForApproval(tx, staff!.id, staff!.email, transactions);
                        const isNotApproved = activeTab === 'pending' ? !tx.employeeApproved : tx.employeeApproved;
                        return isRelevant && isNotApproved;
                      }).sort((a, b) => {
                        const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : new Date(a.timestamp || a.tipDate).getTime();
                        const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : new Date(b.timestamp || b.tipDate).getTime();
                        return timeB - timeA;
                      });
                      const totalSubmitted = filteredTxs.filter(tx => isSubmitted(tx, transactions)).reduce((sum, tx) => sum + (tx.submittedAmount || tx.amount || 0), 0);
                      
                      return (
                        <>
                          {filteredTxs.map((tx) => (
                            <div key={tx.id} className="grid grid-cols-[100px_1fr_100px_1fr_120px] gap-x-4 items-center px-4 py-2.5 hover:bg-gray-50/50 transition-colors text-xs min-w-0">
                              <span className="font-bold text-gray-900 text-left">{tx.tipDate}</span>
                              <div className="text-right whitespace-normal break-all">
                                <span className="font-black text-gray-900">
                                  {formatCurrency(tx.submittedAmount !== undefined ? tx.submittedAmount : (tx.amount || 0))}
                                </span>
                              </div>
                              <span className="text-right truncate text-[9px] text-gray-400 font-mono">#{tx.id.substring(0, 8)}</span>
                              <span className="text-[10px] font-black text-gray-500 uppercase truncate text-left">{tx.shiftName || 'General'}</span>
                              <span className="text-[10px] font-black text-blue-600 uppercase flex items-center gap-1 text-left">
                                {tx.source || 'General'}
                                {tx.employeeApproved && (
                                  <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-[8px] font-bold uppercase tracking-wider inline-flex items-center gap-0.5 whitespace-nowrap">
                                    <ShieldCheck size={8} /> Verified
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}

                          {filteredTxs.length === 0 && (
                            <div className="p-8 text-center text-gray-400 italic text-xs">
                              No unsigned transaction entries remaining for this period.
                            </div>
                          )}

                          <div className="grid grid-cols-[100px_1fr_100px_1fr_120px] gap-x-4 items-center px-4 py-3 bg-gray-50 font-black text-xs border-t border-gray-100 min-w-0">
                            <span className="col-span-1 text-gray-900 uppercase text-left">Period Total</span>
                            <span className="text-right text-emerald-600 font-black">{formatCurrency(totalSubmitted)}</span>
                            <span className="col-span-3"></span>
                          </div>
                        </>
                      );
                    })()}

                    {activePeriod.shortfalls.map((sf) => (
                      <div key={sf.id} className="flex justify-between items-center p-4 bg-red-50/20 hover:bg-red-50/40 transition-colors">
                        <div className="space-y-0.5">
                          <span className="text-xs font-bold text-gray-900 block">{sf.startDate} to {sf.endDate}</span>
                          <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                            <span className="text-red-600 font-bold">Safe-Harbor Shortfall</span>
                            <span>•</span>
                            <span>Method: {sf.allocationMethod.replace('_', ' ')}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-black text-red-600">{formatCurrency(sf.allocatedShortfall)}</span>
                          <span className="text-[9px] text-gray-400 block font-mono">ID: {sf.id?.substring(0, 8)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submitting Audit Trail timestamps if already approved */}
                {activePeriod.isApproved && (
                  <div id="certification-record" className="border-t border-gray-100 pt-6 space-y-4">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sign-off Audit Verification</h4>
                    
                    {/* Combine signatures display */}
                    {(() => {
                      const sampleTx = activePeriod.transactions.find(t => t.employeeApproved) || 
                                       (activePeriod.shortfalls.find(sf => sf.employeeApproved) as any);
                      if (!sampleTx) return null;
                      
                      const approvedAt = sampleTx.employeeApprovalTimestamp?.toDate 
                        ? sampleTx.employeeApprovalTimestamp.toDate() 
                        : new Date(sampleTx.employeeApprovalTimestamp || Date.now());

                      return (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-emerald-50/20 border border-emerald-100 rounded-2xl p-6">
                            <div className="space-y-3">
                              <span className="text-[9px] font-black text-emerald-700 uppercase tracking-widest block">Signature Manifest</span>
                              
                              {sampleTx.employeeApprovalSignatureType === 'type' ? (
                                <div className="bg-white px-6 py-4 border border-emerald-100 rounded-xl flex items-center justify-center min-h-16">
                                  <span className="font-cursive text-2xl text-slate-800 tracking-wide select-none italic font-serif">
                                    {sampleTx.employeeApprovalSignatureText}
                                  </span>
                                </div>
                              ) : (
                                <div className="bg-white border border-emerald-100 rounded-xl overflow-hidden flex items-center justify-center min-h-16">
                                  <img 
                                    src={sampleTx.employeeApprovalSignatureDrawn} 
                                    alt="Signature" 
                                    className="max-h-14 object-contain"
                                    referrerPolicy="no-referrer"
                                  />
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col justify-center space-y-1 text-xs text-gray-600">
                              <div>
                                <span className="font-bold text-gray-800 uppercase tracking-wider text-[10px]">Verified Employee</span>
                                <p className="font-mono text-gray-600">{user?.email || staff.email}</p>
                              </div>
                              <div className="pt-2">
                                <span className="font-bold text-gray-800 uppercase tracking-wider text-[10px]">Sign-off Timestamp</span>
                                <p className="font-mono text-gray-600">{approvedAt.toLocaleString()}</p>
                              </div>
                              <div className="pt-2 flex items-center gap-1 text-emerald-700 font-bold">
                                <ShieldCheck size={14} /> Standard Audit Pass
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row gap-4">
                            <button 
                              type="button"
                              onClick={() => window.print()}
                              className="flex-1 py-3 bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 text-xs font-bold uppercase tracking-widest rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              <FileCheck size={14} />
                              Download / Print Certification Record
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Signature Interactive Drawer - only shown if unapproved */}
              {!activePeriod.isApproved && (
                <div className="bg-white border border-gray-100 rounded-[2rem] p-8 shadow-sm space-y-6">
                  <div className="flex items-center gap-2 border-b border-gray-50 pb-4">
                    <span className="p-1.5 bg-blue-50 text-blue-700 rounded-lg">
                      <PenTool size={16} />
                    </span>
                    <div>
                      <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">Digital Signature & Certification</h3>
                      <p className="text-xs text-gray-400 font-medium">Certify your tip report accuracy for this payroll period</p>
                    </div>
                  </div>

                  {/* Late Warning */}
                  {compliance.isLate && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3 text-amber-800">
                      <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-xs space-y-1">
                        <strong className="font-bold">Late Sign-off Audit Warning</strong>
                        <p className="leading-relaxed text-amber-700">
                          This sign-off period has passed (deadline: {compliance.deadlineStr}). Please complete your confirmation as soon as possible to ensure your records are updated correctly.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Toggle Sign Type */}
                  <div className="flex justify-center">
                    <div className="bg-gray-100 p-1 rounded-xl flex gap-1">
                      <button
                        type="button"
                        onClick={() => setActiveSignType('type')}
                        className={`px-4 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                          activeSignType === 'type' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-950'
                        }`}
                      >
                        Cursive Typed
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveSignType('draw');
                          setTimeout(clearCanvas, 50);
                        }}
                        className={`px-4 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                          activeSignType === 'draw' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-950'
                        }`}
                      >
                        Draw Signature
                      </button>
                    </div>
                  </div>

                  {/* Input Box or Drawing Stage */}
                  <div className="border border-gray-100 rounded-2xl p-6 bg-gray-50 relative min-h-40 flex items-center justify-center">
                    {activeSignType === 'type' ? (
                      <div className="w-full max-w-md space-y-4 text-center">
                        <input
                          type="text"
                          value={typedName}
                          onChange={(e) => setTypedName(e.target.value)}
                          placeholder="Type your full legal name to generate cursive..."
                          className="w-full text-center py-3 px-4 border-b border-gray-300 bg-transparent text-lg font-medium focus:outline-none focus:border-blue-600 font-sans"
                        />
                        {typedName && (
                          <div className="pt-4 border-t border-gray-100/50">
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Live Cursive Preview</span>
                            <span className="font-cursive text-3xl text-blue-900 tracking-wide select-none italic font-serif">
                              {typedName}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-full max-w-lg flex flex-col items-center space-y-3">
                        <div className="border border-gray-200 bg-white rounded-xl overflow-hidden relative w-full h-36">
                          <canvas
                            ref={canvasRef}
                            width={500}
                            height={144}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                            className="w-full h-full cursor-crosshair touch-none"
                          />
                          <button
                            type="button"
                            onClick={clearCanvas}
                            className="absolute bottom-2 right-2 p-1.5 bg-gray-100 text-gray-500 hover:text-gray-950 rounded-lg hover:bg-gray-250 transition-colors"
                            title="Clear signature canvas"
                          >
                            <Eraser size={14} />
                          </button>
                          <span className="absolute bottom-2 left-2 text-[8px] font-black text-gray-300 uppercase tracking-widest pointer-events-none select-none">
                            Sign with cursor or touch
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirmation Checkbox */}
                  <label className="flex items-start gap-3 p-5 bg-blue-50/20 border border-blue-100 rounded-2xl cursor-pointer hover:bg-blue-50/40 transition-colors">
                    <input
                      type="checkbox"
                      checked={perjuryCertified}
                      onChange={(e) => setPerjuryCertified(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="text-xs space-y-1">
                      <strong className="font-bold text-gray-900 uppercase tracking-wider text-[10px] block">Tip Confirmation & Review</strong>
                      <p className="leading-relaxed text-gray-600">
                        I have reviewed my recorded shift tips and confirm that this summary accurately reflects the cash and POS tips reported for this payroll period.
                      </p>
                    </div>
                  </label>

                  {/* Submit Button */}
                  <button
                    type="button"
                    onClick={handleSignPeriod}
                    disabled={
                      submitting ||
                      !perjuryCertified ||
                      (activeSignType === 'type' && !typedName.trim()) ||
                      (activeSignType === 'draw' && !drawnData)
                    }
                    className={`w-full py-4 rounded-[1.5rem] font-bold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2 ${
                      !perjuryCertified || (activeSignType === 'type' && !typedName.trim()) || (activeSignType === 'draw' && !drawnData)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'
                    }`}
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <FileCheck size={16} /> Sign & Certify Submitted Tips
                      </>
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          ) : (
            <div className="card p-12 text-center text-gray-400 italic bg-white rounded-3xl border border-gray-100">
              Please select a payroll period above to review and sign off.
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

